// lib/taxonomy.ts (UPDATED: includes approved list queries + keeps suggestCategory/suggestSubcategory)
//
// ✅ Keeps normalizeLabel here to avoid circular imports
// ✅ Adds listApprovedCategories + listApprovedSubcategoriesByCategory for onboarding
// ✅ Query logic is tolerant of legacy docs missing isDeleted/sortOrder (filters in code)
// ✅ Still supports moderated "pending" suggestions

import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import type { Firestore } from "firebase/firestore";

const CATEGORIES_COLLECTION = "categories";
const CATEGORY_SUGGESTIONS_COLLECTION = "categorySuggestions";

// ✅ Keep normalizeLabel in this file (taxonomy.ts) to avoid circular imports.
// taxonomyAdmin.ts already imports normalizeLabel from here.
export function normalizeLabel(s: string) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/** ✅ Shared statuses used across admin + onboarding */
export type CategoryStatus = "approved" | "pending" | "rejected";

/** ✅ Types exported so components can import from "@/lib/taxonomy" */
export type CategoryDoc = {
  id: string;
  name: string;
  nameNormalized: string;
  status: CategoryStatus;

  // admin schema
  isDeleted: boolean;
  sortOrder: number;

  createdByUid?: string;
  createdAt?: any;
  updatedAt?: any;
  updatedBy?: string;
};

export type SubcategoryDoc = {
  id: string; // ✅ subId only (matches categories/{categoryId}/subcategories/{subId})
  name: string;
  nameNormalized: string;
  status: CategoryStatus;

  // ✅ required for adminListSubcategoriesGlobal
  categoryId: string;

  // optional UI convenience (not required to be stored)
  categoryName?: string;

  // admin schema
  isDeleted: boolean;
  sortOrder: number;

  createdByUid?: string;
  createdAt?: any;
  updatedAt?: any;
  updatedBy?: string;
};

/* -----------------------------
   ✅ Public (onboarding) queries
------------------------------ */

/**
 * ✅ List approved categories for onboarding selection.
 * - Uses Firestore as source of truth.
 * - Filters out deleted in code to tolerate legacy docs missing isDeleted.
 * - Safe ordering: uses orderBy(sortOrder) but defaults sortOrder for legacy docs.
 *
 * NOTE: If you have many legacy docs missing sortOrder and you see query issues,
 * remove orderBy and sort in code instead.
 */
export async function listApprovedCategories(db: Firestore): Promise<CategoryDoc[]> {
  const q = query(
    collection(db, CATEGORIES_COLLECTION),
    where("status", "==", "approved"),
    orderBy("sortOrder", "asc")
  );

  const snap = await getDocs(q);

  const rows = snap.docs.map((d) => {
    const data: any = d.data();
    const name = String(data?.name ?? data?.category ?? "");
    const norm = String(data?.nameNormalized ?? normalizeLabel(name) ?? d.id);

    const docOut: CategoryDoc = {
      id: d.id,
      name,
      nameNormalized: norm,
      status: (data?.status as CategoryStatus) ?? "approved",

      // tolerate legacy/missing fields
      isDeleted: data?.isDeleted === true,
      sortOrder: typeof data?.sortOrder === "number" ? data.sortOrder : 1000,

      createdByUid: data?.createdByUid,
      createdAt: data?.createdAt,
      updatedAt: data?.updatedAt,
      updatedBy: data?.updatedBy,
    };

    return docOut;
  });

  // tolerate legacy docs where isDeleted missing (treat as not deleted)
  return rows
    .filter((x) => x.status === "approved" && x.isDeleted !== true)
    .sort((a, b) => (a.sortOrder ?? 1000) - (b.sortOrder ?? 1000) || a.name.localeCompare(b.name));
}

/**
 * ✅ List approved subcategories under a category (nested path).
 * - categories/{categoryId}/subcategories/{subId}
 * - Filters deleted in code to tolerate legacy docs missing isDeleted.
 */
export async function listApprovedSubcategoriesByCategory(
  db: Firestore,
  categoryId: string
): Promise<SubcategoryDoc[]> {
  const catId = String(categoryId || "").trim();
  if (!catId) return [];

  const q = query(
    collection(db, CATEGORIES_COLLECTION, catId, "subcategories"),
    where("status", "==", "approved"),
    orderBy("sortOrder", "asc")
  );

  const snap = await getDocs(q);

  const rows = snap.docs.map((d) => {
    const data: any = d.data();
    const name = String(data?.name ?? "");
    const norm = String(data?.nameNormalized ?? normalizeLabel(name) ?? d.id);

    const docOut: SubcategoryDoc = {
      id: d.id, // ✅ subId
      categoryId: String(data?.categoryId ?? catId),
      categoryName: data?.categoryName,

      name,
      nameNormalized: norm,
      status: (data?.status as CategoryStatus) ?? "approved",

      isDeleted: data?.isDeleted === true,
      sortOrder: typeof data?.sortOrder === "number" ? data.sortOrder : 1000,

      createdByUid: data?.createdByUid,
      createdAt: data?.createdAt,
      updatedAt: data?.updatedAt,
      updatedBy: data?.updatedBy,
    };

    return docOut;
  });

  return rows
    .filter((x) => x.status === "approved" && x.isDeleted !== true)
    .sort((a, b) => (a.sortOrder ?? 1000) - (b.sortOrder ?? 1000) || a.name.localeCompare(b.name));
}

/* -----------------------------
   ✅ Moderated suggestions (pending)
------------------------------ */

export async function suggestCategory(
  db: Firestore,
  args: { name: string; createdBy: string }
): Promise<
  | { kind: "existsApproved"; categoryId: string; categoryName: string }
  | { kind: "pendingCreated"; categoryId: string; categoryName: string }
  | { kind: "existsPending"; categoryId: string; categoryName: string }
> {
  const name = String(args.name || "").trim();
  const id = normalizeLabel(name);
  if (!id) throw new Error("Invalid category name.");

  const ref = doc(db, CATEGORIES_COLLECTION, id);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    const data: any = snap.data();
    const existingName = (data.name ?? data.category ?? name).toString();
    const isDeleted = data.isDeleted === true;

    if (data.status === "approved" && !isDeleted) {
      return { kind: "existsApproved", categoryId: id, categoryName: existingName };
    }

    if (data.status === "pending" && !isDeleted) {
      return { kind: "existsPending", categoryId: id, categoryName: existingName };
    }

    if (data.status === "rejected") {
      throw new Error(
        "This category was previously rejected. Please choose another name or contact support."
      );
    }

    if (isDeleted) {
      throw new Error(
        "This category is unavailable. Please choose another name or contact support."
      );
    }

    throw new Error(
      "This category is unavailable. Please choose another name or contact support."
    );
  }

  const suggestionRef = doc(
    db,
    CATEGORY_SUGGESTIONS_COLLECTION,
    `category_${id}`
  );
  const suggestionSnap = await getDoc(suggestionRef);

  if (suggestionSnap.exists()) {
    const data: any = suggestionSnap.data();
    const existingName = (data.categoryName ?? data.name ?? name).toString();

    if (data.status === "approved") {
      return {
        kind: "existsApproved",
        categoryId: id,
        categoryName: existingName,
      };
    }

    if (data.status === "pending") {
      return {
        kind: "existsPending",
        categoryId: id,
        categoryName: existingName,
      };
    }

    if (data.status === "rejected") {
      throw new Error(
        "This category was previously rejected. Please choose another name or contact support."
      );
    }

    throw new Error(
      "This category is unavailable. Please choose another name or contact support."
    );
  }

  await setDoc(
    suggestionRef,
    {
      type: "category",
      categoryId: id,
      categoryName: name,
      name,
      nameNormalized: id,
      status: "pending",

      createdByUid: args.createdBy,
      createdBy: args.createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return { kind: "pendingCreated", categoryId: id, categoryName: name };
}

export async function suggestSubcategory(
  db: Firestore,
  args: { categoryId: string; categoryName?: string; name: string; createdBy: string }
): Promise<
  | { kind: "existsApproved"; subcategoryId: string; subcategoryName: string }
  | { kind: "pendingCreated"; subcategoryId: string; subcategoryName: string }
  | { kind: "existsPending"; subcategoryId: string; subcategoryName: string }
> {
  const categoryId = args.categoryId?.trim();
  if (!categoryId) throw new Error("Missing categoryId.");

  const name = String(args.name || "").trim();
  const subId = normalizeLabel(name);
  if (!subId) throw new Error("Invalid subcategory name.");

  // ✅ doc id is subId only (matches Firestore path)
  const categoryRef = doc(db, CATEGORIES_COLLECTION, categoryId);
  const categorySnap = await getDoc(categoryRef);
  const categoryData: any = categorySnap.exists() ? categorySnap.data() : null;
  const parentCategoryName = categoryData
    ? String(categoryData.name ?? categoryData.category ?? "")
    : String(args.categoryName ?? "");

  const subRef = doc(db, CATEGORIES_COLLECTION, categoryId, "subcategories", subId);
  const snap = await getDoc(subRef);

  if (snap.exists()) {
    const data: any = snap.data();
    const existingName = (data.name ?? name).toString();
    const isDeleted = data.isDeleted === true;

    if (data.status === "approved" && !isDeleted) {
      return {
        kind: "existsApproved",
        subcategoryId: subId,
        subcategoryName: existingName,
      };
    }

    if (data.status === "pending" && !isDeleted) {
      return {
        kind: "existsPending",
        subcategoryId: subId,
        subcategoryName: existingName,
      };
    }

    if (data.status === "rejected") {
      throw new Error(
        "This subcategory was previously rejected. Please choose another name or contact support."
      );
    }

    if (isDeleted) {
      throw new Error(
        "This subcategory is unavailable. Please choose another name or contact support."
      );
    }

    throw new Error(
      "This subcategory is unavailable. Please choose another name or contact support."
    );
  }

  const suggestionRef = doc(
    db,
    CATEGORY_SUGGESTIONS_COLLECTION,
    `subcategory_${categoryId}_${subId}`
  );
  const suggestionSnap = await getDoc(suggestionRef);

  if (suggestionSnap.exists()) {
    const data: any = suggestionSnap.data();
    const existingName = (data.subcategoryName ?? data.name ?? name).toString();

    if (data.status === "approved") {
      return {
        kind: "existsApproved",
        subcategoryId: subId,
        subcategoryName: existingName,
      };
    }

    if (data.status === "pending") {
      return {
        kind: "existsPending",
        subcategoryId: subId,
        subcategoryName: existingName,
      };
    }

    if (data.status === "rejected") {
      throw new Error(
        "This subcategory was previously rejected. Please choose another name or contact support."
      );
    }

    throw new Error(
      "This subcategory is unavailable. Please choose another name or contact support."
    );
  }

  await setDoc(
    suggestionRef,
    {
      type: "subcategory",
      categoryId,
      categoryName: parentCategoryName,
      subcategoryId: subId,
      subcategoryName: name,
      name,
      nameNormalized: subId,
      status: "pending",

      createdByUid: args.createdBy,
      createdBy: args.createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return { kind: "pendingCreated", subcategoryId: subId, subcategoryName: name };
}
